import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  FormControlLabel,
  Switch,
  Button,
  Alert,
  Card,
  CardContent,
  Menu,
  Divider,
  Chip,
  SelectChangeEvent,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Save, Payment, Receipt, Description, PhotoCamera as PhotoCameraIcon, Delete as DeleteIcon, Block, HelpOutline as HelpIcon, AccountBalanceWallet, Assessment, Security } from '@mui/icons-material';
import { configurationService } from '../../services/configurationService';
import { ParametroFinanceiro, GeraNfcomAutomatico, CompetenciaNfcom } from './types';
import FormaPgtoModal from '../../components/modals/FormaPgtoModal';
import MotivoDescontoModal from '../../components/modals/MotivoDescontoModal';
import ImageUploadCrop from '../../components/common/ImageUploadCrop';

const horasBloqueio = [
  { value: 'a_cada_30_minutos', label: 'A cada 30 minutos' },
  { value: 'de_hora_em_hora', label: 'A cada hora' },
  ...Array.from({ length: 24 }, (_, i) => ({
    value: `${String(i).padStart(2, '0')}:00`,
    label: `${String(i).padStart(2, '0')}:00`
  })),
];

const diasSemana = [
  { value: '1', label: 'Domingo' },
  { value: '2', label: 'Segunda-feira' },
  { value: '3', label: 'Terça-feira' },
  { value: '4', label: 'Quarta-feira' },
  { value: '5', label: 'Quinta-feira' },
  { value: '6', label: 'Sexta-feira' },
  { value: '7', label: 'Sábado' }
];

const ParametrosFinanceiro: React.FC = () => {
  const [parametros, setParametros] = useState<ParametroFinanceiro>({
    obrigatorio_caixa_usuario: false,
    fechamento_caixa_diario: false,
    auto_gerar_fatura: false,
    qtde_dias_gerar_fat_auto: 0,
    nao_gerar_fatura_bloqueado: false,
    abater_creditos_criacao: false,
    mes_gera_fatura: 'atual',
    dias_de_vencimento: [],
    dias_minimos_proporcional: 0,
    gera_nfcom_automatico: 'nao_gerar',
    qtde_dias_gerar_nfcom_auto: 0,
    competencia_nfcom: 'data_vencimento',
    boleto_instrucao: 'Multa de 2% por atraso.\nJuros de 0,03% de mora ao dia.\nO pagamento deste boleto não quita débitos anteriores.',
    boleto_juros: '',
    boleto_multa: '',
    exibir_historico_consumo_fatura: false,
    modelo_capa_carne: 'capa01',
    exibir_dados_cliente_capa: false,
    boleto_desconto_tipo: '',
    boleto_desconto_valor: '',
    boleto_desconto_dias: 0,
    transmissao_nfcom_automatica: false,
    suspensao_por_inadimplencia: false,
    meses_inadimplencia: 1,
    desprovisionar_onu_suspensao: false,
    // Bloqueio Automático (Migrado)
    bloqueio_automatico: false,
    qtde_dias_corte: 0,
    dias_semana_corte: [],
    bloqueio_tipo_mensalidade: true,
    nao_bloqueia_feriados: false,
    horario_bloqueio_automatico: '03:00',
    desbloqueio_automatico: false,
    desbloqueio_confianca: false,
    qtde_dias_desbloqueio_confianca: 7
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [formaPgtoModalOpen, setFormaPgtoModalOpen] = useState(false);
  const [motivoDescontoModalOpen, setMotivoDescontoModalOpen] = useState(false);
  const [helpModal, setHelpModal] = useState<'bloqueio' | null>(null);

  // Estados para imagem de aviso na fatura
  const [imagemAviso, setImagemAviso] = useState<string | null>(null);
  const [avisoUploadModalOpen, setAvisoUploadModalOpen] = useState(false);
  const [avisoUploading, setAvisoUploading] = useState(false);
  const [avisoMenuAnchor, setAvisoMenuAnchor] = useState<null | HTMLElement>(null);
  const [imagemCapaCarneCustom, setImagemCapaCarneCustom] = useState<string | null>(null);
  const [capaCarneUploadModalOpen, setCapaCarneUploadModalOpen] = useState(false);
  const [capaCarneUploading, setCapaCarneUploading] = useState(false);
  const [capaCarneMenuAnchor, setCapaCarneMenuAnchor] = useState<null | HTMLElement>(null);
  const [imagemContraCapaCarneCustom, setImagemContraCapaCarneCustom] = useState<string | null>(null);
  const [contraCapaCarneUploadModalOpen, setContraCapaCarneUploadModalOpen] = useState(false);
  const [contraCapaCarneUploading, setContraCapaCarneUploading] = useState(false);
  const [contraCapaCarneMenuAnchor, setContraCapaCarneMenuAnchor] = useState<null | HTMLElement>(null);

  // Carregar configurações do banco
  useEffect(() => {
    loadConfigurations();
    loadImagemAviso();
    loadImagemCapaCarneCustom();
    loadImagemContraCapaCarneCustom();
  }, []);

  const loadConfigurations = async () => {
    try {
      setLoading(true);
      const configs = await configurationService.getAllConfigurations();

      // Mapear configurações para o estado
      const configMap: { [key: string]: string } = {};
      configs.forEach(config => {
        configMap[config.chave] = config.valor;
      });

      setParametros({
        obrigatorio_caixa_usuario: configMap['obrigatorio_caixa_usuario'] === 'true',
        fechamento_caixa_diario: configMap['fechamento_caixa_diario'] === 'true',
        auto_gerar_fatura: configMap['auto_gerar_fatura'] === 'true',
        qtde_dias_gerar_fat_auto: parseInt(configMap['qtde_dias_gerar_fat_auto'] || '0'),
        nao_gerar_fatura_bloqueado: configMap['nao_gerar_fatura_bloqueado'] === 'true',
        abater_creditos_criacao: configMap['abater_creditos_criacao'] === 'true',
        mes_gera_fatura: (configMap['mes_gera_fatura'] as 'atual' | 'proximo') || 'atual',
        dias_de_vencimento: configMap['dias_de_vencimento']
          ? configMap['dias_de_vencimento'].split(',')
          : [],
        dias_minimos_proporcional: parseInt(configMap['dias_minimos_proporcional'] || '0'),
        gera_nfcom_automatico: (configMap['gera_nfcom_automatico'] as GeraNfcomAutomatico) || 'nao_gerar',
        qtde_dias_gerar_nfcom_auto: parseInt(configMap['qtde_dias_gerar_nfcom_auto'] || '0'),
        competencia_nfcom: (configMap['competencia_nfcom'] as CompetenciaNfcom) || 'data_vencimento',
        boleto_instrucao: configMap['boleto_instrucao'] || 'Multa de 2% por atraso.\nJuros de 0,03% de mora ao dia.\nO pagamento deste boleto não quita débitos anteriores.',
        boleto_juros: configMap['boleto_juros'] || '',
        boleto_multa: configMap['boleto_multa'] || '',
        exibir_historico_consumo_fatura: configMap['exibir_historico_consumo_fatura'] === 'true',
        modelo_capa_carne: (configMap['modelo_capa_carne'] as 'capa01' | 'custom' | 'custom_extra01' | 'custom_extra02') || 'capa01',
        exibir_dados_cliente_capa: configMap['exibir_dados_cliente_capa'] === 'true',
        boleto_desconto_tipo: (configMap['boleto_desconto_tipo'] as 'PERCENTAGE' | 'FIXED' | '') || '',
        boleto_desconto_valor: configMap['boleto_desconto_valor'] || '',
        boleto_desconto_dias: parseInt(configMap['boleto_desconto_dias'] || '0'),
        transmissao_nfcom_automatica: configMap['transmissao_nfcom_automatica'] === 'true',
        suspensao_por_inadimplencia: configMap['suspensao_por_inadimplencia'] === 'true',
        meses_inadimplencia: parseInt(configMap['meses_inadimplencia'] || '1'),
        desprovisionar_onu_suspensao: configMap['desprovisionar_onu_suspensao'] === 'true',
        // Bloqueio Automático (Migrado)
        bloqueio_automatico: configMap['bloqueio_automatico'] === 'true',
        qtde_dias_corte: parseInt(configMap['qtde_dias_corte'] || '0'),
        dias_semana_corte: (() => {
          const v = configMap['dias_semana_corte'];
          if (!v) return [];
          try {
            const parsed = JSON.parse(v);
            return Array.isArray(parsed) ? parsed : [];
          } catch (e) {
            return v.split(',').filter(Boolean);
          }
        })(),
        bloqueio_tipo_mensalidade: configMap['bloqueio_tipo_mensalidade'] !== 'false',
        nao_bloqueia_feriados: configMap['nao_bloqueia_feriados'] === 'true',
        horario_bloqueio_automatico: (() => {
          const v = configMap['horario_bloqueio_automatico'] || '03:00';
          if (v === 'a_cada_30_minutos' || v === 'de_hora_em_hora') return v;
          const parts = v.split(':');
          const h = parseInt(parts[0] || '3', 10);
          return `${String(Math.min(23, Math.max(0, h))).padStart(2, '0')}:00`;
        })(),
        desbloqueio_automatico: configMap['desbloqueio_automatico'] === 'true',
        desbloqueio_confianca: configMap['desbloqueio_confianca'] === 'true',
        qtde_dias_desbloqueio_confianca: parseInt(configMap['qtde_dias_desbloqueio_confianca'] || '7'),
      });
    } catch (error: any) {
      console.error('Erro ao carregar configurações financeiras:', error);
      setMessage({ type: 'error', text: 'Erro ao carregar configurações financeiras' });
    } finally {
      setLoading(false);
    }
  };

  const loadImagemAviso = async () => {
    try {
      const data = await configurationService.getImagemFaturaAviso();
      if (data?.imagem) {
        setImagemAviso(data.imagem);
      } else {
        setImagemAviso(null);
      }
    } catch (error) {
      console.error('Erro ao carregar imagem de aviso:', error);
    }
  };

  const loadImagemCapaCarneCustom = async () => {
    try {
      const data = await configurationService.getImagemCapaCarneCustom();
      if (data?.imagem) {
        setImagemCapaCarneCustom(data.imagem);
      } else {
        setImagemCapaCarneCustom(null);
      }
    } catch (error) {
      console.error('Erro ao carregar imagem custom da capa do carnê:', error);
    }
  };

  const loadImagemContraCapaCarneCustom = async () => {
    try {
      const data = await configurationService.getImagemContraCapaCarneCustom();
      if (data?.imagem) {
        setImagemContraCapaCarneCustom(data.imagem);
      } else {
        setImagemContraCapaCarneCustom(null);
      }
    } catch (error) {
      console.error('Erro ao carregar imagem custom da contracapa do carnê:', error);
    }
  };

  const getImagemAvisoUrl = () => {
    if (!imagemAviso) return '';
    return configurationService.getImagemFaturaAvisoUrl(imagemAviso);
  };

  const getImagemCapaCarneCustomUrl = () => {
    if (!imagemCapaCarneCustom) return '';
    return configurationService.getImagemCapaCarneCustomUrl(imagemCapaCarneCustom);
  };

  const getImagemContraCapaCarneCustomUrl = () => {
    if (!imagemContraCapaCarneCustom) return '';
    return configurationService.getImagemContraCapaCarneCustomUrl(imagemContraCapaCarneCustom);
  };

  const handleAvisoUpload = () => {
    setAvisoUploadModalOpen(true);
    setAvisoMenuAnchor(null);
  };

  const handleAvisoRemove = async () => {
    try {
      setAvisoUploading(true);
      const result = await configurationService.removeImagemFaturaAviso();
      if (result.success) {
        setMessage({ type: 'success', text: result.message || 'Imagem de aviso removida com sucesso!' });
        setImagemAviso(null);
      } else {
        setMessage({ type: 'error', text: result.message || 'Erro ao remover imagem' });
      }
    } catch (error: any) {
      console.error('Erro ao remover imagem de aviso:', error);
      setMessage({ type: 'error', text: 'Erro ao remover imagem de aviso' });
    } finally {
      setAvisoUploading(false);
      setAvisoMenuAnchor(null);
    }
  };

  const handleCapaCarneUpload = () => {
    setCapaCarneUploadModalOpen(true);
    setCapaCarneMenuAnchor(null);
  };

  const handleCapaCarneRemove = async () => {
    try {
      setCapaCarneUploading(true);
      const result = await configurationService.removeImagemCapaCarneCustom();
      if (result.success) {
        setMessage({ type: 'success', text: result.message || 'Imagem custom da capa removida com sucesso!' });
        setImagemCapaCarneCustom(null);
      } else {
        setMessage({ type: 'error', text: result.message || 'Erro ao remover imagem custom da capa' });
      }
    } catch (error: any) {
      console.error('Erro ao remover imagem custom da capa:', error);
      setMessage({ type: 'error', text: 'Erro ao remover imagem custom da capa' });
    } finally {
      setCapaCarneUploading(false);
      setCapaCarneMenuAnchor(null);
    }
  };

  const handleContraCapaCarneUpload = () => {
    setContraCapaCarneUploadModalOpen(true);
    setContraCapaCarneMenuAnchor(null);
  };

  const handleContraCapaCarneRemove = async () => {
    try {
      setContraCapaCarneUploading(true);
      const result = await configurationService.removeImagemContraCapaCarneCustom();
      if (result.success) {
        setMessage({ type: 'success', text: result.message || 'Imagem custom da contracapa removida com sucesso!' });
        setImagemContraCapaCarneCustom(null);
      } else {
        setMessage({ type: 'error', text: result.message || 'Erro ao remover imagem custom da contracapa' });
      }
    } catch (error: any) {
      console.error('Erro ao remover imagem custom da contracapa:', error);
      setMessage({ type: 'error', text: 'Erro ao remover imagem custom da contracapa' });
    } finally {
      setContraCapaCarneUploading(false);
      setContraCapaCarneMenuAnchor(null);
    }
  };

  const handleAvisoImageSave = async (croppedImageBlob: Blob) => {
    setAvisoUploading(true);
    try {
      const extension = croppedImageBlob.type === 'image/png' ? 'png' :
        croppedImageBlob.type === 'image/webp' ? 'webp' : 'jpg';
      const file = new File([croppedImageBlob], `imagem-fatura-aviso.${extension}`, {
        type: croppedImageBlob.type
      });
      const response = await configurationService.uploadImagemFaturaAviso(file);
      if (response.success) {
        setMessage({ type: 'success', text: response.message || 'Imagem de aviso enviada com sucesso!' });
        await loadImagemAviso();
      } else {
        setMessage({ type: 'error', text: response.message || 'Erro ao fazer upload da imagem' });
      }
    } catch (error) {
      console.error('Erro ao fazer upload da imagem de aviso:', error);
      setMessage({ type: 'error', text: 'Erro ao fazer upload da imagem de aviso' });
    } finally {
      setAvisoUploading(false);
      setAvisoUploadModalOpen(false);
    }
  };

  const handleCapaCarneImageSave = async (croppedImageBlob: Blob) => {
    setCapaCarneUploading(true);
    try {
      const extension = croppedImageBlob.type === 'image/png' ? 'png' :
        croppedImageBlob.type === 'image/webp' ? 'webp' : 'jpg';
      const file = new File([croppedImageBlob], `imagem-capa-carne-custom.${extension}`, {
        type: croppedImageBlob.type
      });
      const response = await configurationService.uploadImagemCapaCarneCustom(file);
      if (response.success) {
        setMessage({ type: 'success', text: response.message || 'Imagem custom da capa enviada com sucesso!' });
        await loadImagemCapaCarneCustom();
      } else {
        setMessage({ type: 'error', text: response.message || 'Erro ao fazer upload da imagem custom da capa' });
      }
    } catch (error) {
      console.error('Erro ao fazer upload da imagem custom da capa:', error);
      setMessage({ type: 'error', text: 'Erro ao fazer upload da imagem custom da capa' });
    } finally {
      setCapaCarneUploading(false);
      setCapaCarneUploadModalOpen(false);
    }
  };

  const handleContraCapaCarneImageSave = async (croppedImageBlob: Blob) => {
    setContraCapaCarneUploading(true);
    try {
      const extension = croppedImageBlob.type === 'image/png' ? 'png' :
        croppedImageBlob.type === 'image/webp' ? 'webp' : 'jpg';
      const file = new File([croppedImageBlob], `imagem-contra-capa-carne-custom.${extension}`, {
        type: croppedImageBlob.type
      });
      const response = await configurationService.uploadImagemContraCapaCarneCustom(file);
      if (response.success) {
        setMessage({ type: 'success', text: response.message || 'Imagem custom da contracapa enviada com sucesso!' });
        await loadImagemContraCapaCarneCustom();
      } else {
        setMessage({ type: 'error', text: response.message || 'Erro ao fazer upload da imagem custom da contracapa' });
      }
    } catch (error) {
      console.error('Erro ao fazer upload da imagem custom da contracapa:', error);
      setMessage({ type: 'error', text: 'Erro ao fazer upload da imagem custom da contracapa' });
    } finally {
      setContraCapaCarneUploading(false);
      setContraCapaCarneUploadModalOpen(false);
    }
  };

  const handleInputChange = (field: keyof ParametroFinanceiro, value: any) => {
    setParametros(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Formata para exibição: "2.00" → "2,00"
  const formatarPercentualDisplay = (value: string): string => {
    if (!value) return '';
    return value.replace('.', ',');
  };

  // Ao digitar: aceita apenas números e vírgula/ponto, armazena com ponto
  const handlePercentualChange = (field: keyof ParametroFinanceiro, inputValue: string) => {
    // Remove tudo exceto dígitos, vírgula e ponto
    const clean = inputValue.replace(/[^\d,\.]/g, '');
    // Normaliza separador decimal para ponto
    const normalized = clean.replace(',', '.');
    handleInputChange(field, normalized);
  };

  // Ao sair do campo: formata com 2 casas decimais
  const handlePercentualBlur = (field: keyof ParametroFinanceiro, value: string) => {
    if (!value) return;
    const num = parseFloat(value.replace(',', '.'));
    if (!isNaN(num)) {
      handleInputChange(field, num.toFixed(2));
    }
  };

  const handleDiasCorteChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    handleInputChange('dias_semana_corte', typeof value === 'string' ? value.split(',') : value);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setMessage(null);

      // Mapear campos para chaves de configuração
      const configUpdates = [
        { chave: 'obrigatorio_caixa_usuario', valor: parametros.obrigatorio_caixa_usuario.toString() },
        { chave: 'fechamento_caixa_diario', valor: parametros.fechamento_caixa_diario.toString() },
        { chave: 'auto_gerar_fatura', valor: parametros.auto_gerar_fatura.toString() },
        { chave: 'qtde_dias_gerar_fat_auto', valor: parametros.qtde_dias_gerar_fat_auto.toString() },
        { chave: 'nao_gerar_fatura_bloqueado', valor: parametros.nao_gerar_fatura_bloqueado.toString() },
        { chave: 'abater_creditos_criacao', valor: parametros.abater_creditos_criacao.toString() },
        { chave: 'mes_gera_fatura', valor: parametros.mes_gera_fatura },
        { chave: 'dias_de_vencimento', valor: parametros.dias_de_vencimento.join(',') },
        { chave: 'dias_minimos_proporcional', valor: parametros.dias_minimos_proporcional.toString() },
        { chave: 'gera_nfcom_automatico', valor: parametros.gera_nfcom_automatico },
        { chave: 'qtde_dias_gerar_nfcom_auto', valor: parametros.qtde_dias_gerar_nfcom_auto.toString() },
        { chave: 'competencia_nfcom', valor: parametros.competencia_nfcom },
        { chave: 'boleto_instrucao', valor: parametros.boleto_instrucao },
        { chave: 'boleto_juros', valor: parametros.boleto_juros },
        { chave: 'boleto_multa', valor: parametros.boleto_multa },
        { chave: 'exibir_historico_consumo_fatura', valor: parametros.exibir_historico_consumo_fatura.toString() },
        { chave: 'modelo_capa_carne', valor: parametros.modelo_capa_carne },
        { chave: 'exibir_dados_cliente_capa', valor: parametros.exibir_dados_cliente_capa.toString() },
        { chave: 'boleto_desconto_tipo', valor: parametros.boleto_desconto_tipo },
        { chave: 'boleto_desconto_valor', valor: parametros.boleto_desconto_valor },
        { chave: 'boleto_desconto_dias', valor: parametros.boleto_desconto_dias.toString() },
        { chave: 'transmissao_nfcom_automatica', valor: parametros.transmissao_nfcom_automatica.toString() },
        { chave: 'suspensao_por_inadimplencia', valor: parametros.suspensao_por_inadimplencia.toString() },
        { chave: 'meses_inadimplencia', valor: parametros.meses_inadimplencia.toString() },
        { chave: 'desprovisionar_onu_suspensao', valor: parametros.desprovisionar_onu_suspensao.toString() },
        // Bloqueio Automático (Migrado)
        { chave: 'bloqueio_automatico', valor: parametros.bloqueio_automatico.toString() },
        { chave: 'qtde_dias_corte', valor: parametros.qtde_dias_corte.toString() },
        { chave: 'dias_semana_corte', valor: JSON.stringify(parametros.dias_semana_corte) },
        { chave: 'bloqueio_tipo_mensalidade', valor: parametros.bloqueio_tipo_mensalidade.toString() },
        { chave: 'nao_bloqueia_feriados', valor: parametros.nao_bloqueia_feriados.toString() },
        { chave: 'horario_bloqueio_automatico', valor: parametros.horario_bloqueio_automatico || '03:00' },
        { chave: 'desbloqueio_automatico', valor: parametros.desbloqueio_automatico.toString() },
        { chave: 'desbloqueio_confianca', valor: parametros.desbloqueio_confianca.toString() },
        { chave: 'qtde_dias_desbloqueio_confianca', valor: parametros.qtde_dias_desbloqueio_confianca.toString() },
      ];

      // Atualizar cada configuração
      for (const config of configUpdates) {
        await configurationService.updateConfiguration(config.chave, { valor: config.valor });
      }

      setMessage({ type: 'success', text: 'Configurações financeiras salvas com sucesso!' });
    } catch (error: any) {
      console.error('Erro ao salvar configurações financeiras:', error);
      setMessage({ type: 'error', text: error.message || 'Erro ao salvar configurações financeiras' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ pb: 8 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight="bold" color="primary">
            Parâmetros Financeiros
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure as regras de faturamento, caixa, inadimplência e notas fiscais.
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Save />}
          onClick={handleSave}
          disabled={loading}
          sx={{ borderRadius: 2, px: 3 }}
        >
          {loading ? 'Salvando...' : 'Salvar Alterações'}
        </Button>
      </Box>

      {message && (
        <Alert severity={message.type} sx={{ mb: 3, borderRadius: 2 }}>
          {message.text}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Card 1: Faturamento e Ciclo de Vida */}
        <Grid item xs={12} lg={8}>
          <Card sx={{ borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <AccountBalanceWallet color="primary" />
                <Typography variant="h6" fontWeight="bold">Faturamento e Ciclo de Vida</Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Mês para Geração de Fatura</InputLabel>
                    <Select
                      value={parametros.mes_gera_fatura}
                      onChange={(e) => handleInputChange('mes_gera_fatura', e.target.value)}
                      label="Mês para Geração de Fatura"
                    >
                      <MenuItem value="atual">Mês Atual</MenuItem>
                      <MenuItem value="proximo">Próximo Mês</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Dias de Vencimento Disponíveis</InputLabel>
                    <Select
                      multiple
                      value={parametros.dias_de_vencimento}
                      onChange={(e) => handleInputChange('dias_de_vencimento', e.target.value)}
                      label="Dias de Vencimento Disponíveis"
                      renderValue={(selected) => (selected as string[]).sort((a, b) => parseInt(a) - parseInt(b)).join(', ')}
                    >
                      {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                        <MenuItem key={day} value={day.toString().padStart(2, '0')}>
                          Dia {day.toString().padStart(2, '0')}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Dias Mínimos para Proporcional"
                    type="number"
                    value={parametros.dias_minimos_proporcional}
                    onChange={(e) => handleInputChange('dias_minimos_proporcional', parseInt(e.target.value) || 0)}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">dias</InputAdornment>,
                    }}
                    inputProps={{ min: 0, max: 30 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Agrupar proporcional se for menor que X dias.
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={parametros.auto_gerar_fatura}
                          onChange={(e) => handleInputChange('auto_gerar_fatura', e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Geração Automática de Faturas"
                    />
                    {parametros.auto_gerar_fatura && (
                      <TextField
                        size="small"
                        label="Dias Antes do Venc."
                        type="number"
                        value={parametros.qtde_dias_gerar_fat_auto}
                        onChange={(e) => handleInputChange('qtde_dias_gerar_fat_auto', parseInt(e.target.value) || 0)}
                        sx={{ mt: 1, maxWidth: 180 }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">dias</InputAdornment>,
                        }}
                      />
                    )}
                    {parametros.auto_gerar_fatura && (
                      <>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={parametros.nao_gerar_fatura_bloqueado}
                              onChange={(e) => handleInputChange('nao_gerar_fatura_bloqueado', e.target.checked)}
                              color="warning"
                              size="small"
                            />
                          }
                          label={<Typography variant="caption">Não gerar p/ contratos bloqueados</Typography>}
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={parametros.abater_creditos_criacao}
                              onChange={(e) => handleInputChange('abater_creditos_criacao', e.target.checked)}
                              color="primary"
                              size="small"
                            />
                          }
                          label={<Typography variant="caption">Abater créditos na criação</Typography>}
                        />
                      </>
                    )}
                  </Box>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                {imagemAviso ? (
                  <img
                    src={getImagemAvisoUrl()}
                    alt="Aviso na fatura"
                    style={{
                      maxWidth: 160,
                      maxHeight: 100,
                      cursor: 'pointer',
                      borderRadius: 4,
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                      objectFit: 'contain'
                    }}
                    onClick={(e) => setAvisoMenuAnchor(e.currentTarget)}
                  />
                ) : (
                  <Box
                    onClick={(e) => setAvisoMenuAnchor(e.currentTarget)}
                    sx={{
                      width: 160,
                      height: 80,
                      border: '2px dashed #ccc',
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      bgcolor: 'background.paper',
                      '&:hover': { borderColor: 'primary.main' }
                    }}
                  >
                    <PhotoCameraIcon color="disabled" />
                  </Box>
                )}
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle2" fontWeight="bold">Imagem de aviso na fatura</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Será exibida no PDF da fatura para comunicados gerais.
                  </Typography>
                  <Button size="small" variant="text" onClick={handleAvisoUpload} sx={{ mt: 0.5 }}>
                    {imagemAviso ? 'Alterar Imagem' : 'Adicionar Imagem'}
                  </Button>
                </Box>
                <Menu
                  anchorEl={avisoMenuAnchor}
                  open={Boolean(avisoMenuAnchor)}
                  onClose={() => setAvisoMenuAnchor(null)}
                >
                  <MenuItem onClick={handleAvisoUpload}>Alterar</MenuItem>
                  {imagemAviso && <MenuItem onClick={handleAvisoRemove} sx={{ color: 'error.main' }}>Remover</MenuItem>}
                </Menu>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Card 2: Segurança e Caixa */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ height: '100%', borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <Security color="primary" />
                <Typography variant="h6" fontWeight="bold">Segurança e Caixa</Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={parametros.obrigatorio_caixa_usuario}
                        onChange={(e) => handleInputChange('obrigatorio_caixa_usuario', e.target.checked)}
                        color="secondary"
                      />
                    }
                    label={<Typography variant="body2" fontWeight="medium">Obrigatório Caixa p/ Usuário</Typography>}
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6 }}>
                    Exige que o operador tenha um caixa aberto para receber pagamentos.
                  </Typography>
                </Box>

                <Box sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={parametros.fechamento_caixa_diario}
                        onChange={(e) => handleInputChange('fechamento_caixa_diario', e.target.checked)}
                        color="secondary"
                      />
                    }
                    label={<Typography variant="body2" fontWeight="medium">Fechamento de Caixa Diário</Typography>}
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6 }}>
                    Obriga o fechamento do caixa ao final do expediente.
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Card 3: Inadimplência e Bloqueio Automático */}
        <Grid item xs={12}>
          <Card sx={{ borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)', borderLeft: '6px solid', borderLeftColor: 'error.main' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Block color="error" />
                  <Typography variant="h6" fontWeight="bold">Inadimplência e Bloqueio Automático</Typography>
                  <Tooltip title="Informações sobre regras de bloqueio">
                    <IconButton size="small" onClick={() => setHelpModal('bloqueio')}>
                      <HelpIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              <Grid container spacing={4}>
                {/* Coluna 1: Suspensão Administrativa */}
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 2.5, bgcolor: 'error.light', color: 'error.contrastText', borderRadius: 2, mb: 2, opacity: 0.9 }}>
                    <Typography variant="subtitle2" fontWeight="bold">Suspensão Administrativa</Typography>
                    <Typography variant="caption">Afeta o status do contrato e faturamento.</Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={parametros.suspensao_por_inadimplencia}
                        onChange={(e) => handleInputChange('suspensao_por_inadimplencia', e.target.checked)}
                        color="error"
                      />
                    }
                    label="Ativar Suspensão Automática"
                  />
                  {parametros.suspensao_por_inadimplencia && (
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Meses de Atraso"
                        type="number"
                        value={parametros.meses_inadimplencia}
                        onChange={(e) => handleInputChange('meses_inadimplencia', parseInt(e.target.value) || 0)}
                        InputProps={{ endAdornment: <InputAdornment position="end">meses</InputAdornment> }}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={parametros.desprovisionar_onu_suspensao}
                            onChange={(e) => handleInputChange('desprovisionar_onu_suspensao', e.target.checked)}
                            color="error"
                          />
                        }
                        label="Desprovisionar ONU/ONT"
                      />
                    </Box>
                  )}
                </Grid>

                {/* Coluna 2: Bloqueio Técnico */}
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 2.5, bgcolor: 'primary.main', color: 'primary.contrastText', borderRadius: 2, mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold">Bloqueio Técnico (Radius)</Typography>
                    <Typography variant="caption">Corta a navegação via eletrônica.</Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={parametros.bloqueio_automatico}
                        onChange={(e) => handleInputChange('bloqueio_automatico', e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Ativar Bloqueio Automático"
                  />
                  {parametros.bloqueio_automatico && (
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Dias de Execução</InputLabel>
                        <Select
                          multiple
                          value={parametros.dias_semana_corte}
                          onChange={handleDiasCorteChange}
                          label="Dias de Execução"
                          renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selected.map((value) => {
                                const dia = diasSemana.find(d => d.value === value);
                                return <Chip key={value} label={dia?.label.charAt(0) || value} size="small" />;
                              })}
                            </Box>
                          )}
                        >
                          {diasSemana.map((dia) => (
                            <MenuItem key={dia.value} value={dia.value}>{dia.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Horário"
                          select
                          value={parametros.horario_bloqueio_automatico}
                          onChange={(e) => handleInputChange('horario_bloqueio_automatico', e.target.value)}
                        >
                          {horasBloqueio.map((h) => (
                            <MenuItem key={h.value} value={h.value}>{h.label}</MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          fullWidth
                          size="small"
                          label="Dias Atraso"
                          type="number"
                          value={parametros.qtde_dias_corte}
                          onChange={(e) => handleInputChange('qtde_dias_corte', parseInt(e.target.value) || 0)}
                        />
                      </Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={parametros.bloqueio_tipo_mensalidade}
                            onChange={(e) => handleInputChange('bloqueio_tipo_mensalidade', e.target.checked)}
                            size="small"
                          />
                        }
                        label={<Typography variant="caption">Apenas Mensalidades</Typography>}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={parametros.nao_bloqueia_feriados}
                            onChange={(e) => handleInputChange('nao_bloqueia_feriados', e.target.checked)}
                            size="small"
                          />
                        }
                        label={<Typography variant="caption">Não bloquear em Feriados</Typography>}
                      />
                    </Box>
                  )}
                </Grid>

                {/* Coluna 3: Desbloqueio */}
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 2.5, bgcolor: 'success.main', color: 'success.contrastText', borderRadius: 2, mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold">Regras de Desbloqueio</Typography>
                    <Typography variant="caption">Liberação automática ou manual.</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={parametros.desbloqueio_automatico}
                          onChange={(e) => handleInputChange('desbloqueio_automatico', e.target.checked)}
                          color="success"
                        />
                      }
                      label="Desbloqueio Automático"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={parametros.desbloqueio_confianca}
                          onChange={(e) => handleInputChange('desbloqueio_confianca', e.target.checked)}
                          color="secondary"
                        />
                      }
                      label="Desbloqueio por Confiança"
                    />
                    {parametros.desbloqueio_confianca && (
                      <TextField
                        fullWidth
                        size="small"
                        label="Dias de Confiança"
                        type="number"
                        value={parametros.qtde_dias_desbloqueio_confianca}
                        onChange={(e) => handleInputChange('qtde_dias_desbloqueio_confianca', parseInt(e.target.value) || 1)}
                        InputProps={{ endAdornment: <InputAdornment position="end">dias</InputAdornment> }}
                      />
                    )}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Card 4: NFCom */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ height: '100%', borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <Assessment color="primary" />
                <Typography variant="h6" fontWeight="bold">NFCom (Nota Fiscal Eletrônica)</Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Geração Automática</InputLabel>
                    <Select
                      value={parametros.gera_nfcom_automatico}
                      onChange={(e) => handleInputChange('gera_nfcom_automatico', e.target.value as GeraNfcomAutomatico)}
                      label="Geração Automática"
                    >
                      <MenuItem value="nao_gerar">Desativado</MenuItem>
                      <MenuItem value="antes_vencimento">Dias antes do vencimento</MenuItem>
                      <MenuItem value="apos_pagamento">Dias após o pagamento</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {parametros.gera_nfcom_automatico !== 'nao_gerar' && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Janela de Execução"
                      type="number"
                      value={parametros.qtde_dias_gerar_nfcom_auto}
                      onChange={(e) => handleInputChange('qtde_dias_gerar_nfcom_auto', parseInt(e.target.value) || 0)}
                      InputProps={{ endAdornment: <InputAdornment position="end">dias</InputAdornment> }}
                    />
                  </Grid>
                )}

                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Regra de Competência</InputLabel>
                    <Select
                      value={parametros.competencia_nfcom}
                      onChange={(e) => handleInputChange('competencia_nfcom', e.target.value as CompetenciaNfcom)}
                      label="Regra de Competência"
                    >
                      <MenuItem value="data_vencimento">Data de Vencimento</MenuItem>
                      <MenuItem value="data_pagamento">Data de Pagamento</MenuItem>
                      <MenuItem value="data_emissao">Data de Emissão</MenuItem>
                      <MenuItem value="referencia_fatura">Período de Referência</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={parametros.transmissao_nfcom_automatica}
                        onChange={(e) => handleInputChange('transmissao_nfcom_automatica', e.target.checked)}
                        color="success"
                      />
                    }
                    label="Transmissão Automática (cron)"
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6, mb: 1 }}>
                    Ativa a transmissão automática de NFCom para o Sefaz às 3:00 da madrugada.
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Card 5: Regras de Juros e Multa */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ height: '100%', borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <Description color="primary" />
                <Typography variant="h6" fontWeight="bold">Regras do Boleto</Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Instruções Padrão"
                    multiline
                    rows={3}
                    value={parametros.boleto_instrucao}
                    onChange={(e) => handleInputChange('boleto_instrucao', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Juros"
                    size="small"
                    value={formatarPercentualDisplay(parametros.boleto_juros)}
                    onChange={(e) => handlePercentualChange('boleto_juros', e.target.value)}
                    onBlur={(e) => handlePercentualBlur('boleto_juros', e.target.value)}
                    placeholder="1,00"
                    inputProps={{ inputMode: 'decimal' }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                    helperText="Percentual ao Mês"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Multa por atraso"
                    size="small"
                    value={formatarPercentualDisplay(parametros.boleto_multa)}
                    onChange={(e) => handlePercentualChange('boleto_multa', e.target.value)}
                    onBlur={(e) => handlePercentualBlur('boleto_multa', e.target.value)}
                    placeholder="2,00"
                    inputProps={{ inputMode: 'decimal' }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                    helperText="Percentual único sobre o valor"
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <FormControlLabel
                control={
                  <Switch
                    checked={parametros.exibir_historico_consumo_fatura}
                    onChange={(e) => handleInputChange('exibir_historico_consumo_fatura', e.target.checked)}
                    color="primary"
                  />
                }
                label={<Typography variant="body2" fontWeight="medium">Exibir Histórico de Consumo na Fatura</Typography>}
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6, mb: 1 }}>
                Mostra o gráfico de consumo (Download/Upload) dos últimos 6 meses no PDF da fatura.
              </Typography>
              <Divider sx={{ my: 2 }} />
              {/* Desconto por Pontualidade */}
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Desconto por Pontualidade
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Tipo de Desconto</InputLabel>
                    <Select
                      value={parametros.boleto_desconto_tipo}
                      onChange={(e) => handleInputChange('boleto_desconto_tipo', e.target.value)}
                      label="Tipo de Desconto"
                    >
                      <MenuItem value=""><em>Desativado</em></MenuItem>
                      <MenuItem value="PERCENTAGE">Percentual (%)</MenuItem>
                      <MenuItem value="FIXED">Valor Fixo (R$)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {parametros.boleto_desconto_tipo !== '' && (
                  <>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label={parametros.boleto_desconto_tipo === 'PERCENTAGE' ? 'Desconto' : 'Desconto (R$)'}
                        size="small"
                        value={
                          parametros.boleto_desconto_tipo === 'PERCENTAGE'
                            ? formatarPercentualDisplay(parametros.boleto_desconto_valor)
                            : parametros.boleto_desconto_valor.replace('.', ',')
                        }
                        onChange={(e) => {
                          if (parametros.boleto_desconto_tipo === 'PERCENTAGE') {
                            handlePercentualChange('boleto_desconto_valor', e.target.value);
                          } else {
                            const clean = e.target.value.replace(/[^\d,\.]/g, '').replace(',', '.');
                            handleInputChange('boleto_desconto_valor', clean);
                          }
                        }}
                        onBlur={(e) => handlePercentualBlur('boleto_desconto_valor', e.target.value)}
                        placeholder={parametros.boleto_desconto_tipo === 'PERCENTAGE' ? '5,00' : '10,00'}
                        inputProps={{ inputMode: 'decimal' }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              {parametros.boleto_desconto_tipo === 'PERCENTAGE' ? '%' : 'R$'}
                            </InputAdornment>
                          ),
                        }}
                        helperText={
                          parametros.boleto_desconto_tipo === 'PERCENTAGE'
                            ? 'Percentual sobre o valor'
                            : 'Valor fixo em reais'
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Dias antes do vencimento"
                        size="small"
                        type="number"
                        value={parametros.boleto_desconto_dias}
                        onChange={(e) => handleInputChange('boleto_desconto_dias', parseInt(e.target.value) || 0)}
                        inputProps={{ min: 0, max: 365 }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">dias</InputAdornment>,
                        }}
                        helperText="Limite para aplicar o desconto"
                      />
                    </Grid>
                  </>
                )}
              </Grid>
              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>
                Capa do Carnê
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Modelo da Capa</InputLabel>
                    <Select
                      value={parametros.modelo_capa_carne}
                      onChange={(e) => handleInputChange('modelo_capa_carne', e.target.value)}
                      label="Modelo da Capa"
                    >
                      <MenuItem value="capa01">capa01</MenuItem>
                      <MenuItem value="custom">custom</MenuItem>
                      <MenuItem value="custom_extra01">custom_extra01</MenuItem>
                      <MenuItem value="custom_extra02">custom_extra02</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={parametros.exibir_dados_cliente_capa}
                        onChange={(e) => handleInputChange('exibir_dados_cliente_capa', e.target.checked)}
                        color="primary"
                      />
                    }
                    label={<Typography variant="body2" fontWeight="medium">Exibir dados do cliente na capa</Typography>}
                  />
                </Grid>
              </Grid>

              {(parametros.modelo_capa_carne === 'custom' || parametros.modelo_capa_carne === 'custom_extra01' || parametros.modelo_capa_carne === 'custom_extra02') && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                    {imagemCapaCarneCustom ? (
                      <img
                        src={getImagemCapaCarneCustomUrl()}
                        alt="Imagem custom da capa do carnê"
                        style={{
                          maxWidth: 180,
                          maxHeight: 100,
                          cursor: 'pointer',
                          borderRadius: 4,
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                          objectFit: 'cover'
                        }}
                        onClick={(e) => setCapaCarneMenuAnchor(e.currentTarget)}
                      />
                    ) : (
                      <Box
                        onClick={(e) => setCapaCarneMenuAnchor(e.currentTarget)}
                        sx={{
                          width: 180,
                          height: 100,
                          border: '2px dashed #ccc',
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          bgcolor: 'background.paper',
                          '&:hover': { borderColor: 'primary.main' }
                        }}
                      >
                        <PhotoCameraIcon color="disabled" />
                      </Box>
                    )}
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle2" fontWeight="bold">Imagem custom da capa</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        A imagem ocupa 100% da largura e altura da seção da capa no PDF.
                      </Typography>
                      <Button size="small" variant="text" onClick={handleCapaCarneUpload} sx={{ mt: 0.5 }} disabled={capaCarneUploading}>
                        {imagemCapaCarneCustom ? 'Alterar Imagem' : 'Adicionar Imagem'}
                      </Button>
                    </Box>
                    <Menu
                      anchorEl={capaCarneMenuAnchor}
                      open={Boolean(capaCarneMenuAnchor)}
                      onClose={() => setCapaCarneMenuAnchor(null)}
                    >
                      <MenuItem onClick={handleCapaCarneUpload} disabled={capaCarneUploading}>Alterar</MenuItem>
                      {imagemCapaCarneCustom && <MenuItem onClick={handleCapaCarneRemove} disabled={capaCarneUploading} sx={{ color: 'error.main' }}>Remover</MenuItem>}
                    </Menu>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                    {imagemContraCapaCarneCustom ? (
                      <img
                        src={getImagemContraCapaCarneCustomUrl()}
                        alt="Imagem custom da contracapa do carnê"
                        style={{
                          maxWidth: 180,
                          maxHeight: 100,
                          cursor: 'pointer',
                          borderRadius: 4,
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                          objectFit: 'cover'
                        }}
                        onClick={(e) => setContraCapaCarneMenuAnchor(e.currentTarget)}
                      />
                    ) : (
                      <Box
                        onClick={(e) => setContraCapaCarneMenuAnchor(e.currentTarget)}
                        sx={{
                          width: 180,
                          height: 100,
                          border: '2px dashed #ccc',
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          bgcolor: 'background.paper',
                          '&:hover': { borderColor: 'primary.main' }
                        }}
                      >
                        <PhotoCameraIcon color="disabled" />
                      </Box>
                    )}
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle2" fontWeight="bold">Imagem custom da contracapa</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        A imagem ocupa 100% da largura e altura da seção da contracapa no PDF.
                      </Typography>
                      <Button size="small" variant="text" onClick={handleContraCapaCarneUpload} sx={{ mt: 0.5 }} disabled={contraCapaCarneUploading}>
                        {imagemContraCapaCarneCustom ? 'Alterar Imagem' : 'Adicionar Imagem'}
                      </Button>
                    </Box>
                    <Menu
                      anchorEl={contraCapaCarneMenuAnchor}
                      open={Boolean(contraCapaCarneMenuAnchor)}
                      onClose={() => setContraCapaCarneMenuAnchor(null)}
                    >
                      <MenuItem onClick={handleContraCapaCarneUpload} disabled={contraCapaCarneUploading}>Alterar</MenuItem>
                      {imagemContraCapaCarneCustom && <MenuItem onClick={handleContraCapaCarneRemove} disabled={contraCapaCarneUploading} sx={{ color: 'error.main' }}>Remover</MenuItem>}
                    </Menu>
                  </Box>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  startIcon={<Payment />}
                  onClick={() => setFormaPgtoModalOpen(true)}
                  sx={{ borderRadius: 1.5 }}
                >
                  Meios de PGTO
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  startIcon={<Receipt />}
                  onClick={() => setMotivoDescontoModalOpen(true)}
                  sx={{ borderRadius: 1.5 }}
                >
                  Motivos Desconto
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>


      {/* Modal de Formas de Pagamento */}
      <FormaPgtoModal
        open={formaPgtoModalOpen}
        onClose={() => setFormaPgtoModalOpen(false)}
      />

      {/* Modal de Motivos de Desconto */}
      <MotivoDescontoModal
        open={motivoDescontoModalOpen}
        onClose={() => setMotivoDescontoModalOpen(false)}
      />

      {/* Modal de upload da imagem de aviso na fatura */}
      <ImageUploadCrop
        open={avisoUploadModalOpen}
        onClose={() => setAvisoUploadModalOpen(false)}
        onImageSave={handleAvisoImageSave}
        title="Imagem de aviso na fatura"
        //aspectRatio={16 / 9}
        //maxWidth={800}
        maxHeight={150}
        quality={0.9}
        outputFormat="auto"
        circularCrop={false}
        minCropSize={100}
      />

      <ImageUploadCrop
        open={capaCarneUploadModalOpen}
        onClose={() => setCapaCarneUploadModalOpen(false)}
        onImageSave={handleCapaCarneImageSave}
        title="Imagem custom da capa do carnê"
        quality={0.92}
        outputFormat="auto"
        circularCrop={false}
        minCropSize={100}
      />

      <ImageUploadCrop
        open={contraCapaCarneUploadModalOpen}
        onClose={() => setContraCapaCarneUploadModalOpen(false)}
        onImageSave={handleContraCapaCarneImageSave}
        title="Imagem custom da contracapa do carnê"
        quality={0.92}
        outputFormat="auto"
        circularCrop={false}
        minCropSize={100}
      />

      {/* Modal de Ajuda - Bloqueio */}
      <Dialog open={helpModal === 'bloqueio'} onClose={() => setHelpModal(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HelpIcon color="primary" /> Configurações de Inadimplência e Bloqueio
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Esta seção centraliza todas as regras de consequências financeiras por falta de pagamento.
          </Typography>
          <Typography variant="h6" gutterBottom color="error" sx={{ mt: 2 }}>Suspensão Administrativa</Typography>
          <Typography variant="body2" paragraph>
            A suspensão é uma medida mais rigorosa que o bloqueio eletrônico. Ela altera o status do contrato ou do cliente para "Suspenso", o que pode implicar em cessação de faturamento ou outras medidas administrativas.
            Defina a quantidade de meses (mensalidades) em atraso para que isso ocorra. <strong>Importante:</strong> Clientes a 1 mês de serem suspensos serão classificados como "Próximo da Suspensão" nos relatórios e dashboards.
          </Typography>

          <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>Bloqueio Automático (Radius/Eletrônico)</Typography>
          <Typography variant="body2" paragraph>
            O bloqueio automático envia um comando para o servidor de acesso (Radius/Mikrotik) para impedir a navegação do cliente.
          </Typography>
          <Box component="ul" sx={{ pl: 2 }}>
            <li><strong>Dias de Bloqueio:</strong> Selecione em quais dias da semana o sistema pode executar bloqueios automáticos.</li>
            <li><strong>Qtde de Dias:</strong> Quantos dias após o vencimento da fatura o cliente deve ser bloqueado.</li>
            <li><strong>Horário:</strong> Recomenda-se configurar para horários de madrugada (ex: 03:00) para evitar sobrecarga no horário de pico.</li>
            <li><strong>Apenas Mensalidades:</strong> Se marcado, faturas de serviços ou outros tipos não causarão bloqueio.</li>
          </Box>

          <Typography variant="h6" gutterBottom color="success.main" sx={{ mt: 2 }}>Desbloqueio</Typography>
          <Typography variant="body2" paragraph>
            <strong>Desbloqueio Automático:</strong> Assim que o sistema detectar a baixa do pagamento (via retorno bancário ou baixa manual), o cliente será desbloqueado eletronicamente em poucos minutos.
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>Desbloqueio por Confiança:</strong> Permite que o cliente solicite um desbloqueio temporário (promessa de pagamento) através da central do assinante ou aplicativo. O prazo de validade é definido no campo "Dias de Confiança".
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpModal(null)} color="primary" variant="contained">Entendi</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ParametrosFinanceiro; 